import { useState, useEffect } from 'react';
import { Plus, X, CheckCircle, Lightbulb } from 'lucide-react';
import type { PlanStepProps } from '../types';

const suggestedFeatures = [
  'basicReports',
  'advancedReports',
  'apiAccess',
  'customBranding',
  'multipleAdmins',
  'prioritySupport',
  'autoBackup',
  'multiLanguage',
  'mobileAccess',
  'unlimitedIntegrations',
];

const featureLabels: Record<string, string> = {
  basicReports: 'Reportes básicos',
  advancedReports: 'Reportes avanzados',
  apiAccess: 'Acceso API',
  customBranding: 'Personalización de marca',
  multipleAdmins: 'Múltiples administradores',
  prioritySupport: 'Soporte prioritario 24/7',
  autoBackup: 'Backup automático',
  multiLanguage: 'Multi-idioma',
  mobileAccess: 'Acceso móvil',
  unlimitedIntegrations: 'Integraciones ilimitadas',
};

export function FeaturesStep({ data, onChange, onValidChange }: PlanStepProps) {
  const [newFeatureKey, setNewFeatureKey] = useState('');

  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  const features = data.features ?? {};
  const featureEntries = Object.entries(features);
  const enabledFeatures = featureEntries.filter(([, v]) => v);

  const toggleFeature = (key: string) => {
    const newFeatures = { ...features };
    if (key in newFeatures) {
      newFeatures[key] = !newFeatures[key];
    } else {
      newFeatures[key] = true;
    }
    onChange({ features: newFeatures });
  };

  const removeFeature = (key: string) => {
    const newFeatures = { ...features };
    delete newFeatures[key];
    onChange({ features: newFeatures });
  };

  const addCustomFeature = () => {
    const key = newFeatureKey.trim().replace(/\s+/g, '_').toLowerCase();
    if (key && !(key in features)) {
      onChange({ features: { ...features, [key]: true } });
      setNewFeatureKey('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomFeature();
    }
  };

  const unusedSuggestions = suggestedFeatures.filter((f) => !(f in features));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Características del Plan
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Activa las características que incluye este plan
        </p>
      </div>

      {/* Add Custom Feature */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newFeatureKey}
          onChange={(e) => setNewFeatureKey(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="feature_key (ej: customDashboards)"
          className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                   focus:ring-2 focus:ring-brand-500 focus:border-transparent
                   placeholder:text-gray-400 dark:placeholder:text-gray-500"
        />
        <button
          type="button"
          onClick={addCustomFeature}
          disabled={!newFeatureKey.trim()}
          className="px-4 py-2.5 bg-brand-500 text-white rounded-lg font-medium
                   hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed
                   flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Agregar
        </button>
      </div>

      {/* Feature List */}
      <div className="space-y-2">
        {featureEntries.length === 0 ? (
          <div className="p-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-center">
            <CheckCircle className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              No hay características agregadas
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Usa las sugerencias o agrega características personalizadas
            </p>
          </div>
        ) : (
          featureEntries.map(([key, enabled]) => (
            <div
              key={key}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg group"
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFeature(key)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    enabled
                      ? 'bg-emerald-500 border-emerald-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {enabled && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                </button>
                <div>
                  <span className={`text-gray-800 dark:text-white ${!enabled ? 'line-through opacity-50' : ''}`}>
                    {featureLabels[key] || key}
                  </span>
                  {featureLabels[key] && (
                    <span className="ml-2 text-xs text-gray-400">{key}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFeature(key)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20
                         rounded-lg opacity-0 group-hover:opacity-100 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Suggestions */}
      {unusedSuggestions.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Sugerencias
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {unusedSuggestions.slice(0, 6).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => onChange({ features: { ...features, [key]: true } })}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                         text-gray-700 dark:text-gray-300 rounded-full hover:border-brand-500 hover:text-brand-600
                         dark:hover:text-brand-400 transition-colors"
              >
                + {featureLabels[key] || key}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Counter */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <span className="font-semibold text-gray-800 dark:text-white">
            {enabledFeatures.length}
          </span>{' '}
          característica{enabledFeatures.length !== 1 ? 's' : ''} activa{enabledFeatures.length !== 1 ? 's' : ''}
          {featureEntries.length > enabledFeatures.length && (
            <span className="text-gray-400">
              {' '}({featureEntries.length - enabledFeatures.length} desactivada{featureEntries.length - enabledFeatures.length !== 1 ? 's' : ''})
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
